import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Users, AlertCircle, Plus, Trash2, Search, ExternalLink, Upload } from 'lucide-react';
import { leadsListService } from '../lib/supabaseServices';

const LeadsListManagementModal = ({ isOpen, onClose, darkMode, leadsList, onLeadsListUpdated }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingLead, setIsAddingLead] = useState(false);
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [newLead, setNewLead] = useState({ username: '', displayName: '' });
  const [bulkLeads, setBulkLeads] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [listName, setListName] = useState('');
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  // Load leads when modal opens
  useEffect(() => {
    if (isOpen && leadsList) {
      loadLeads();
      setListName(leadsList.name || '');
    }
  }, [isOpen, leadsList]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const leadsData = await leadsListService.getLeadsFromList(leadsList.id, 1000); // Get more leads
      setLeads(leadsData);
    } catch (err) {
      console.error('Error loading leads:', err);
      setError(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    if (!newLead.username.trim()) {
      setError('Please enter an Instagram username');
      return;
    }

    // Remove @ if user included it
    const cleanUsername = newLead.username.replace('@', '').trim();

    try {
      setIsSaving(true);
      setError(null);

      await leadsListService.addLeadToList(
        leadsList.id, 
        cleanUsername, 
        newLead.displayName.trim() || null
      );

      // Refresh leads
      await loadLeads();
      
      // Reset form
      setNewLead({ username: '', displayName: '' });
      setIsAddingLead(false);
      
      // Notify parent to refresh list counts
      onLeadsListUpdated?.(leadsList.id);
    } catch (err) {
      console.error('Error adding lead:', err);
      setError(err.message || 'Failed to add lead');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkAddLeads = async () => {
    if (!bulkLeads.trim()) {
      setError('Please enter usernames to add');
      return;
    }

    // Parse usernames - split by newlines and clean up
    const usernames = bulkLeads
      .split('\n')
      .map(line => line.replace('@', '').trim())
      .filter(username => username.length > 0);

    if (usernames.length === 0) {
      setError('No valid usernames found');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      let successCount = 0;
      let errorCount = 0;

      // Add each username individually
      for (const username of usernames) {
        try {
          await leadsListService.addLeadToList(leadsList.id, username, null);
          successCount++;
        } catch (err) {
          // If it's a duplicate, skip silently
          if (err.message.includes('duplicate') || err.code === '23505') {
            continue;
          }
          errorCount++;
          console.error(`Error adding ${username}:`, err);
        }
      }

      // Show results
      if (successCount > 0) {
        // Refresh leads
        await loadLeads();
        
        // Reset form
        setBulkLeads('');
        setIsBulkAdding(false);
        
        // Notify parent to refresh list counts
        onLeadsListUpdated?.(leadsList.id);
      }

      if (errorCount > 0) {
        setError(`Added ${successCount} leads successfully. ${errorCount} failed to add.`);
      } else if (successCount === 0) {
        setError('All usernames were already in the list or invalid');
      }

    } catch (err) {
      console.error('Error bulk adding leads:', err);
      setError(err.message || 'Failed to add leads');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateListName = async () => {
    if (!listName.trim() || listName === leadsList.name) {
      setIsEditingName(false);
      setListName(leadsList.name);
      return;
    }

    try {
      setIsUpdatingName(true);
      setError(null);

      await leadsListService.updateLeadsList(leadsList.id, {
        name: listName.trim()
      });

      setIsEditingName(false);
      
      // Notify parent to refresh list
      onLeadsListUpdated?.(leadsList.id);
    } catch (err) {
      console.error('Error updating list name:', err);
      setError(err.message || 'Failed to update list name');
      setListName(leadsList.name); // Reset to original name
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleRemoveLead = async (leadId) => {
    try {
      setError(null);
      await leadsListService.removeLeadFromList(leadId);
      
      // Remove from local state
      setLeads(prev => prev.filter(lead => lead.id !== leadId));
      
      // Notify parent to refresh list counts
      onLeadsListUpdated?.(leadsList.id);
    } catch (err) {
      console.error('Error removing lead:', err);
      setError(err.message || 'Failed to remove lead');
    }
  };

  const handleUpdateLead = async (leadId, updates) => {
    try {
      setError(null);
      await leadsListService.updateLead(leadId, updates);
      
      // Update local state
      setLeads(prev => prev.map(lead => 
        lead.id === leadId ? { ...lead, ...updates } : lead
      ));
    } catch (err) {
      console.error('Error updating lead:', err);
      setError(err.message || 'Failed to update lead');
    }
  };

  const filteredLeads = leads.filter(lead =>
    lead.instagram_username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.display_name && lead.display_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleClose = () => {
    onClose();
    // Reset state after animation
    setTimeout(() => {
      setLeads([]);
      setSearchTerm('');
      setError(null);
      setIsAddingLead(false);
      setIsBulkAdding(false);
      setNewLead({ username: '', displayName: '' });
      setBulkLeads('');
      setIsEditingName(false);
      setListName('');
    }, 200);
  };

  if (!isOpen || !leadsList) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={`${
            darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'
          } rounded shadow-2xl border max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col`}
        >
          {/* Header */}
          <div className={`${darkMode ? 'bg-surface border-border' : 'bg-gray-50 border-gray-200'} px-6 py-4 border-b flex items-center justify-between flex-shrink-0`}>
            <div className="flex items-center space-x-3 flex-1">
              <div className={`w-10 h-10 ${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-accent to-accent-hover'} rounded flex items-center justify-center`}>
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                {isEditingName ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={listName}
                      onChange={(e) => setListName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateListName();
                        if (e.key === 'Escape') {
                          setIsEditingName(false);
                          setListName(leadsList.name);
                        }
                      }}
                      className={`text-lg font-semibold bg-transparent border-b-2 ${
                        darkMode ? 'text-primary border-accent focus:border-accent-hover' : 'text-gray-900 border-accent focus:border-accent-hover'
                      } outline-none px-1 py-1`}
                      autoFocus
                    />
                    <button
                      onClick={handleUpdateListName}
                      disabled={isUpdatingName}
                      className={`p-1 ${darkMode ? 'text-accent hover:text-accent-hover' : 'text-accent hover:text-accent-hover'} transition-colors`}
                    >
                      {isUpdatingName ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => setIsEditingName(true)}
                    className={`cursor-pointer group ${darkMode ? 'hover:text-accent' : 'hover:text-accent'} transition-colors`}
                  >
                    <h2 className={`text-lg font-semibold ${darkMode ? 'text-primary group-hover:text-accent' : 'text-gray-900 group-hover:text-accent'} transition-colors`}>
                      {listName}
                    </h2>
                  </div>
                )}
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                  {leads.length} leads
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 ${darkMode ? 'text-secondary hover:text-primary hover:bg-elevated' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'} rounded transition-colors`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search and Add Bar */}
          <div className={`${darkMode ? 'bg-surface border-border' : 'bg-gray-50 border-gray-200'} px-6 py-3 border-b flex-shrink-0`}>
            <div className="flex space-x-3">
              <div className="flex-1 relative">
                <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-9 pr-4 py-2 w-full border rounded focus:ring-2 focus:border-transparent text-sm ${
                    darkMode 
                      ? 'bg-elevated border-border text-primary placeholder-secondary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                  }`}
                />
              </div>
              <button
                onClick={() => setIsAddingLead(true)}
                className={`px-4 py-2 ${darkMode ? 'bg-accent hover:bg-accent-hover' : 'bg-accent hover:bg-accent-hover'} text-primary rounded transition-colors flex items-center space-x-2 text-sm font-medium`}
              >
                <Plus className="w-4 h-4" />
                <span>Add Lead</span>
              </button>
              <button
                onClick={() => setIsBulkAdding(true)}
                className={`px-4 py-2 ${darkMode ? 'bg-purple-500 hover:bg-purple-600' : 'bg-purple-500 hover:bg-purple-600'} text-white rounded transition-colors flex items-center space-x-2 text-sm font-medium`}
              >
                <Upload className="w-4 h-4" />
                <span>Bulk Add</span>
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className={`mx-6 mt-4 p-3 ${darkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} border rounded flex items-center space-x-2`}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Add Lead Form */}
          {isAddingLead && (
            <div className={`mx-6 mt-4 p-4 ${darkMode ? 'bg-surface border-border' : 'bg-gray-50 border-gray-200'} border rounded`}>
              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`}>Add New Lead</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Instagram username (without @)"
                  value={newLead.username}
                  onChange={(e) => setNewLead(prev => ({ ...prev, username: e.target.value }))}
                  className={`px-3 py-2 border rounded focus:ring-2 focus:border-transparent text-sm ${
                    darkMode 
                      ? 'bg-elevated border-border text-primary placeholder-secondary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                  }`}
                />
                <input
                  type="text"
                  placeholder="Display name (optional)"
                  value={newLead.displayName}
                  onChange={(e) => setNewLead(prev => ({ ...prev, displayName: e.target.value }))}
                  className={`px-3 py-2 border rounded focus:ring-2 focus:border-transparent text-sm ${
                    darkMode 
                      ? 'bg-elevated border-border text-primary placeholder-secondary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                  }`}
                />
              </div>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={handleAddLead}
                  disabled={isSaving || !newLead.username.trim()}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors flex items-center space-x-2 ${
                    isSaving || !newLead.username.trim()
                      ? darkMode ? 'bg-elevated text-muted' : 'bg-gray-200 text-gray-400'
                      : darkMode ? 'bg-accent hover:bg-accent-hover text-primary' : 'bg-accent hover:bg-accent-hover text-primary'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-3 h-3" />
                      <span>Add</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsAddingLead(false);
                    setNewLead({ username: '', displayName: '' });
                    setError(null);
                  }}
                  className={`px-4 py-2 ${darkMode ? 'bg-elevated hover:bg-elevated/80 text-primary' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded transition-colors text-sm font-medium`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Bulk Add Form */}
          {isBulkAdding && (
            <div className={`mx-6 mt-4 p-4 ${darkMode ? 'bg-surface border-border' : 'bg-gray-50 border-gray-200'} border rounded`}>
              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`}>Bulk Add Leads</h3>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-3`}>
                Paste usernames below (one per line, without @):
              </p>
              <textarea
                placeholder={`test\ntest1\ntest2`}
                value={bulkLeads}
                onChange={(e) => setBulkLeads(e.target.value)}
                rows={6}
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:border-transparent text-sm resize-none ${
                  darkMode 
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:ring-cyan-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500'
                }`}
              />
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={handleBulkAddLeads}
                  disabled={isSaving || !bulkLeads.trim()}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors flex items-center space-x-2 ${
                    isSaving || !bulkLeads.trim()
                      ? darkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-200 text-gray-400'
                      : darkMode ? 'bg-purple-500 hover:bg-purple-600 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white'
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-3 h-3" />
                      <span>Add All</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsBulkAdding(false);
                    setBulkLeads('');
                    setError(null);
                  }}
                  className={`px-4 py-2 ${darkMode ? 'bg-elevated hover:bg-elevated/80 text-primary' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded transition-colors text-sm font-medium`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Loading State */}
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className={`${darkMode ? 'bg-surface' : 'bg-gray-100'} rounded p-4 animate-pulse`}>
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded-full`}></div>
                      <div className="flex-1">
                        <div className={`w-32 h-4 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded mb-1`}></div>
                        <div className={`w-24 h-3 ${darkMode ? 'bg-elevated' : 'bg-gray-200'} rounded`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium mb-1">
                  {searchTerm ? 'No leads found' : 'No leads available'}
                </p>
                <p className="text-sm">
                  {searchTerm 
                    ? 'Try adjusting your search terms' 
                    : 'Click "Add Lead" to start building your list'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-4`}>
                  Showing {filteredLeads.length} of {leads.length} leads
                </div>
                {filteredLeads.map((lead, index) => (
                  <motion.div
                    key={lead.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className={`${
                      darkMode ? 'bg-surface hover:bg-elevated' : 'bg-gray-50 hover:bg-gray-100'
                    } rounded p-4 transition-colors group`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={`w-10 h-10 ${darkMode ? 'bg-gradient-to-r from-accent to-accent-hover' : 'bg-gradient-to-r from-accent to-accent-hover'} rounded-full flex items-center justify-center`}>
                          <span className="text-white font-semibold text-sm">
                            {lead.instagram_username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            @{lead.instagram_username}
                          </div>
                          {lead.display_name && (
                            <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                              {lead.display_name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <a
                          href={`https://instagram.com/${lead.instagram_username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`p-2 ${darkMode ? 'text-secondary hover:text-accent' : 'text-gray-400 hover:text-accent'} rounded transition-colors`}
                          title="View Instagram Profile"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleRemoveLead(lead.id)}
                          className={`p-2 ${darkMode ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' : 'text-red-600 hover:text-red-700 hover:bg-red-50'} rounded transition-colors`}
                          title="Remove from list"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LeadsListManagementModal;